/*
 * Copyright (c) 2023. RoZetta Technology Pty Ltd. All rights reserved.
 */

import * as React from "react";
import {
	Tabs,
	Tab,
	Box
} from "@mui/material";
import Alert from '@mui/material/Alert';
import ScenarioEditor from "../components/scenario-modelling/ScenarioEditor";
import PipelineEditor from "../components/scenario-modelling/PipelineEditor";
import AssumptionEditor from "../components/scenario-modelling/AssumptionEditor";
import ScenarioOutput from "../components/scenario-modelling/ScenarioOutput";
import TabPanel from "../components/TabPanel";
import { apiRequest } from "../services/utils/HttpUtils";
import { RunModel } from "../graphql/queries_assumptions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";
import { caveatIntro } from "../assets/texts/pageTextContent";




function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		"aria-controls": `simple-tabpanel-${index}`,
	};
}

export default function ScenarioModelling() {
	const [tabIndex, setTabIndex] = React.useState(0);
	const [scenarioID, setScenarioID] = React.useState("");
	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setTabIndex(newValue);
	};

	const client = useQueryClient();
	const { instance, accounts } = useMsal();
	const { mutate: runScenarioModel, error, isLoading } = useMutation(
		["RunModel"], {
		mutationFn: () => {
			return apiRequest({
				query: RunModel,
				variables: { inScenarioID: scenarioID },
				instance: instance,
				accounts: accounts
			});
		},
		onSuccess: () => {
			client.invalidateQueries(["plot"]).then();
		},
	});

	return (
		<Box className='tabBox-root' sx={{ Height: "80%" }}>
			<Box className='tabSelectorBox'>
				<Tabs value={tabIndex} onChange={handleChange} indicatorColor="secondary"
					textColor="inherit"
				>
					<Tab label="Start" {...a11yProps(0)} disabled={tabIndex == 0} />
					<Tab label="Projects" {...a11yProps(1)} disabled={tabIndex == 0} />
					<Tab label="Assumptions" {...a11yProps(2)} disabled={tabIndex == 0} />
					<Tab label="Output" {...a11yProps(3)} disabled={tabIndex == 0} />
				</Tabs>
			</Box>
			{(!!error && !isLoading) && <Alert severity="warning">
				Unable to retrieve updated model result now.
				Please try again later
			</Alert>}
			<TabPanel value={tabIndex} index={0}>
				<ScenarioEditor setTabIndex={setTabIndex} setScenarioID={setScenarioID} />
			</TabPanel>
			<TabPanel value={tabIndex} index={1}>
				<PipelineEditor
					setTabIndex={setTabIndex}
					scenarioID={scenarioID}
					runModel={runScenarioModel}
				/>
			</TabPanel>
			<TabPanel value={tabIndex} index={2}>
				<AssumptionEditor
					setTabIndex={setTabIndex}
					scenarioID={scenarioID}
					runModel={runScenarioModel} />
			</TabPanel>
			<TabPanel value={tabIndex} index={3}>
				<ScenarioOutput setTabIndex={setTabIndex} scenarioID={scenarioID} modelFlag={isLoading} />
				{/* Add the message below the ScenarioOutput */}
				<Box sx={{ fontStyle: "italic", fontWeight: 700, mt: 2, fontFamily: "ProximaNova-Bold" }}>
					<strong>{caveatIntro}</strong>
				</Box>
			</TabPanel>
		</Box>
	);
}
